import { useEffect, useState } from 'react'
import { leagues, organizations, auth, announcements } from '../lib/api'
import { useNavigate, Link } from 'react-router-dom'
import SetupWizard from '../components/SetupWizard'
import { buildSubdomainUrl, getSubdomain } from '../lib/utils'
import { useUser } from '../contexts/UserContext'
import { useToast } from '../contexts/ToastContext'
import DelayedSpinner from '../components/DelayedSpinner'
import PageHeader from '../components/layout/PageHeader'
import { parseLocalDate, formatTime } from '../utils/dateHelpers'
import PaymentProcessorSettings from '../components/settings/PaymentProcessorSettings'

export default function Dashboard() {
  const navigate = useNavigate()
  const { user } = useUser()
  const toast = useToast()
  const [orgLeagues, setOrgLeagues] = useState([])
  const [organization, setOrganization] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showLeagueForm, setShowLeagueForm] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  })
  const [showSetupWizard, setShowSetupWizard] = useState(false)
  const [hasOrgs, setHasOrgs] = useState(false)
  const [setupDismissed, setSetupDismissed] = useState(false)
  const [canCreateOrg, setCanCreateOrg] = useState(null)
  const [canCreateOrgReason, setCanCreateOrgReason] = useState(null)
  const [canCreateOrgMessage, setCanCreateOrgMessage] = useState(null)

  // Org editing state
  const [editingOrg, setEditingOrg] = useState(false)
  const [orgName, setOrgName] = useState('')
  const [savingOrg, setSavingOrg] = useState(false)

  // Sub requests state
  const [subRequests, setSubRequests] = useState([])
  const [respondingTo, setRespondingTo] = useState(null)
  const [respondMessage, setRespondMessage] = useState('')
  const [respondLoading, setRespondLoading] = useState(false)

  useEffect(() => {
    // Redirect main domain users to their org subdomain
    const currentSubdomain = getSubdomain()
    if (user && !currentSubdomain) {
      const redirectToOrgSubdomain = async () => {
        try {
          const userOrgs = await organizations.getAll()
          if (userOrgs && userOrgs.length > 0) {
            const org = userOrgs[0]
            if (org.slug) {
              const subdomainUrl = buildSubdomainUrl(org.slug) + '/dashboard'
              window.location.href = subdomainUrl
              return
            }
          }
          fetchData()
        } catch (error) {
          console.error('Error fetching organizations for redirect:', error)
          fetchData()
        }
      }
      redirectToOrgSubdomain()
      return
    }

    if (user) {
      fetchData()
    }
  }, [user])

  useEffect(() => {
    const handleOrgChange = () => {
      if (user) {
        fetchData()
      }
    }
    window.addEventListener('organizationChanged', handleOrgChange)
    window.addEventListener('leaguesChanged', handleOrgChange)

    return () => {
      window.removeEventListener('organizationChanged', handleOrgChange)
      window.removeEventListener('leaguesChanged', handleOrgChange)
    }
  }, [user])

  // Check for organizations and org creation eligibility
  useEffect(() => {
    const checkOrganizations = async () => {
      if (!user) {
        return
      }

      try {
        const [canCreateResponse, orgs] = await Promise.all([
          auth.canCreateOrg(),
          organizations.getAll()
        ])

        const hasAnyOrgs = orgs && orgs.length > 0

        setHasOrgs(hasAnyOrgs)
        setCanCreateOrg(canCreateResponse.canCreateOrg)
        setCanCreateOrgReason(canCreateResponse.reason)
        setCanCreateOrgMessage(canCreateResponse.message)

        if (canCreateResponse.canCreateOrg && !hasAnyOrgs && !setupDismissed) {
          setShowSetupWizard(true)
        }
      } catch (error) {
        console.error('Error checking organizations:', error)
      }
    }

    checkOrganizations()
  }, [user, setupDismissed])

  const fetchData = async () => {
    try {
      const [leaguesData, orgsData] = await Promise.all([
        leagues.getAll(true),
        organizations.getAll()
      ])

      const activeLeagues = leaguesData.filter(l => l.archived !== 1)
      setOrgLeagues(activeLeagues)

      if (orgsData && orgsData.length > 0) {
        setOrganization(orgsData[0])
      }

      // Fetch sub requests from all leagues
      const allSubRequests = await Promise.all(
        activeLeagues.map(l =>
          announcements.getSubRequests(l.id).catch(() => [])
        )
      )
      // Flatten and add league info
      const flatSubRequests = allSubRequests
        .flat()
        .filter(sr => sr && sr.sub_status === 'open')
        .map((sr, idx, arr) => {
          const league = activeLeagues.find(l => l.id === sr.league_id)
          return { ...sr, league_name: league?.name }
        })
        .sort((a, b) => new Date(a.game_date) - new Date(b.game_date))
      setSubRequests(flatSubRequests)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Handle responding to a sub request
  const handleSubResponse = async () => {
    if (!respondingTo || !user) return
    setRespondLoading(true)
    try {
      await announcements.respondToSubRequest(respondingTo.id, respondMessage)
      toast.success('You have been added as a sub!')
      // Refresh sub requests
      setSubRequests(prev => prev.filter(sr => sr.id !== respondingTo.id))
      setRespondingTo(null)
      setRespondMessage('')
    } catch (error) {
      console.error('Error responding to sub request:', error)
      toast.error(error.message || 'Failed to respond to sub request')
    } finally {
      setRespondLoading(false)
    }
  }

  const handleCreateLeague = async (e) => {
    e.preventDefault()
    try {
      const response = await leagues.create(formData)
      setFormData({ name: '', description: '' })
      setShowLeagueForm(false)
      window.dispatchEvent(new CustomEvent('leaguesChanged'))
      const path = response.slug ? `/${response.slug}/admin` : `/leagues/${response.id}/admin`
      navigate(path)
    } catch (error) {
      toast.error('Error creating league: ' + error.message)
    }
  }

  const navigateToLeague = (league) => {
    const path = league.slug ? `/${league.slug}` : `/leagues/${league.id}`
    navigate(path)
  }

  const handleEditOrg = () => {
    setOrgName(organization?.name || '')
    setEditingOrg(true)
  }

  const handleSaveOrg = async (e) => {
    e.preventDefault()
    if (!orgName.trim()) {
      toast.error('Organization name is required')
      return
    }

    setSavingOrg(true)
    try {
      const result = await organizations.update(organization.id, {
        name: orgName.trim(),
        description: organization.description,
      })

      toast.success('Organization updated')

      // If slug changed, redirect to new subdomain
      if (result.slugChanged && result.slug) {
        const newUrl = buildSubdomainUrl(result.slug) + '/dashboard'
        toast.success('Redirecting to new URL...')
        setTimeout(() => {
          window.location.href = newUrl
        }, 1000)
      } else {
        setOrganization({ ...organization, name: orgName.trim() })
        setEditingOrg(false)
      }
    } catch (error) {
      toast.error('Error updating organization: ' + error.message)
    } finally {
      setSavingOrg(false)
    }
  }

  if (loading) {
    return <DelayedSpinner delay={200} message="Loading..." />
  }

  const isManager = user?.role === 'org_manager' || user?.role === 'league_manager'
  // Check org membership role OR if user is the organization owner
  const isOrgOwnerOrAdmin = organization?.user_role === 'owner' ||
                            organization?.user_role === 'admin' ||
                            organization?.owner_id === user?.id ||
                            isManager

  return (
    <div>
      {/* Setup Wizard Modal */}
      {showSetupWizard && (
        <SetupWizard
          onComplete={() => {
            setShowSetupWizard(false)
            setSetupDismissed(false)
            if (user) {
              fetchData()
            }
            window.dispatchEvent(new CustomEvent('organizationChanged'))
          }}
          onDismiss={() => {
            setShowSetupWizard(false)
            setSetupDismissed(true)
          }}
        />
      )}

      {/* Banner for users who cannot create organizations */}
      {!hasOrgs && canCreateOrg === false && (
        <div className="card mb-6 border-l-4 border-yellow-500 bg-yellow-50">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 mb-2">
                {canCreateOrgReason === 'existing_owner' ? 'Organization Limit Reached' :
                 canCreateOrgReason === 'existing_player' ? 'Welcome to Your League' :
                 canCreateOrgReason === 'player_role' ? 'Player Account' :
                 'Organization Setup Not Available'}
              </h3>
              <p className="text-sm text-slate-700">
                {canCreateOrgMessage || 'You are not eligible to create a new organization at this time.'}
              </p>
              {(canCreateOrgReason === 'existing_player' || canCreateOrgReason === 'player_role') && (
                <p className="text-sm text-slate-600 mt-2">
                  As a player, you can discover and join teams through the "Find Teams" option in the sidebar.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Call-to-Action Banner for Incomplete Setup */}
      {!hasOrgs && canCreateOrg === true && setupDismissed && !showSetupWizard && (
        <div className="card mb-6 border-l-4 border-ice-600 bg-ice-50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Complete Your Setup
              </h3>
              <p className="text-sm text-slate-700 mb-3">
                Get started by creating your organization, league, and season.
              </p>
              <button
                onClick={() => setShowSetupWizard(true)}
                className="btn-primary btn-sm"
              >
                Start Setup Wizard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organization Header */}
      {editingOrg ? (
        <div className="card mb-6 border-2 border-ice-200 bg-ice-50">
          <h3 className="font-semibold text-slate-900 mb-4">Edit Organization Name</h3>
          <form onSubmit={handleSaveOrg} className="space-y-4">
            <div>
              <label className="label">Organization Name *</label>
              <input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="input w-full"
                placeholder="Enter organization name"
                required
                autoFocus
              />
              <p className="text-xs text-slate-500 mt-1">
                Changing the name will update your subdomain URL
              </p>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary" disabled={savingOrg}>
                {savingOrg ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => setEditingOrg(false)}
                className="btn-secondary"
                disabled={savingOrg}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{organization?.name || 'My Organization'}</h1>
            <p className="text-slate-600 mt-1">Manage your leagues and organization settings</p>
          </div>
          {isManager && organization && (
            <button
              onClick={handleEditOrg}
              className="btn-secondary btn-sm"
              title="Edit organization name"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
          )}
        </div>
      )}

      {/* Quick Stats */}
      {orgLeagues.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="card-static p-4 text-center">
            <div className="text-3xl font-bold text-ice-600">{orgLeagues.length}</div>
            <div className="text-sm text-slate-600">Leagues</div>
          </div>
          <div className="card-static p-4 text-center">
            <div className="text-3xl font-bold text-slate-900">
              {orgLeagues.reduce((sum, l) => sum + (l.team_count || 0), 0)}
            </div>
            <div className="text-sm text-slate-600">Teams</div>
          </div>
          <div className="card-static p-4 text-center">
            <div className="text-3xl font-bold text-slate-900">
              {orgLeagues.reduce((sum, l) => sum + (l.player_count || 0), 0)}
            </div>
            <div className="text-sm text-slate-600">Players</div>
          </div>
          <div className="card-static p-4 text-center">
            <div className="text-3xl font-bold text-green-600">
              {orgLeagues.filter(l => l.active_season_name).length}
            </div>
            <div className="text-sm text-slate-600">Active Seasons</div>
          </div>
        </div>
      )}

      {/* Sub Requests Alert */}
      {subRequests.length > 0 && (
        <div className="mb-6">
          <div className="card border-l-4 border-l-orange-500">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <h2 className="text-lg font-bold text-slate-900">Sub Requests ({subRequests.length})</h2>
            </div>
            <div className="space-y-3">
              {subRequests.slice(0, 5).map((sub) => (
                <div key={sub.id} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-0.5 rounded">
                          {sub.league_name}
                        </span>
                        <span className="text-sm font-medium text-slate-900">Sub Needed</span>
                      </div>
                      <p className="text-sm text-slate-700 mt-1">
                        <span className="font-medium">{sub.home_team_name}</span>
                        <span className="text-slate-500"> vs </span>
                        <span className="font-medium">{sub.away_team_name}</span>
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        {sub.game_date && parseLocalDate(sub.game_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        {sub.game_time && ` at ${formatTime(sub.game_time)}`}
                        {sub.rink_name && ` - ${sub.rink_name}`}
                      </p>
                      {sub.message && (
                        <p className="text-xs text-slate-500 mt-1 italic">"{sub.message}"</p>
                      )}
                    </div>
                    <button
                      onClick={() => setRespondingTo(sub)}
                      className="btn-warning btn-sm flex-shrink-0"
                    >
                      I Can Sub
                    </button>
                  </div>
                </div>
              ))}
              {subRequests.length > 5 && (
                <p className="text-sm text-slate-500 text-center">
                  + {subRequests.length - 5} more sub requests
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Leagues Grid */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Your Leagues</h2>
          {isManager && (
            <button
              onClick={() => setShowLeagueForm(!showLeagueForm)}
              className="btn-primary btn-sm"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New League
            </button>
          )}
        </div>

        {/* Create League Form */}
        {showLeagueForm && (
          <div className="card mb-6 border-2 border-ice-200 bg-ice-50">
            <h3 className="font-semibold text-slate-900 mb-4">Create New League</h3>
            <form onSubmit={handleCreateLeague} className="space-y-4">
              <div>
                <label className="label">League Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., Sunday Night Hockey"
                  required
                />
              </div>
              <div>
                <label className="label">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input w-full"
                  rows="2"
                  placeholder="Brief description of the league..."
                />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn-primary">
                  Create League
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowLeagueForm(false)
                    setFormData({ name: '', description: '' })
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* League Cards */}
        {orgLeagues.length === 0 ? (
          <div className="card text-center py-12">
            <svg className="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Leagues Yet</h3>
            <p className="text-slate-600 mb-4">Create your first league to get started.</p>
            {isManager && (
              <button
                onClick={() => setShowLeagueForm(true)}
                className="btn-primary"
              >
                Create Your First League
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {orgLeagues.map((league) => (
              <button
                key={league.id}
                onClick={() => navigateToLeague(league)}
                className="card hover:shadow-lg transition-all text-left group"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-lg text-slate-900 group-hover:text-ice-600 transition-colors">
                    {league.name}
                  </h3>
                  <svg className="w-5 h-5 text-slate-400 group-hover:text-ice-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>

                {league.description && (
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{league.description}</p>
                )}

                <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {league.team_count || 0} teams
                  </span>
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {league.player_count || 0} players
                  </span>
                </div>

                {league.active_season_name ? (
                  <div className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                    {league.active_season_name}
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>
                    No active season
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Payment Processing Section */}
        {isOrgOwnerOrAdmin && organization && (
          <div className="mt-8">
            <PaymentProcessorSettings organizationId={organization.id} />
          </div>
        )}

        {/* Automated Notifications Info - for managers */}
        {isManager && orgLeagues.length > 0 && (
          <div className="mt-8 card bg-gradient-to-br from-ice-50 to-blue-50 border-ice-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-ice-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-ice-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-2">IceOps Keeps Everyone in the Loop</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Your players and team captains automatically receive notifications as you use the app. The more features you use, the more value everyone gets!
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <span className="text-sm font-medium text-slate-900">Game Reminders</span>
                        <p className="text-xs text-slate-500">Players get automated reminders before games</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <span className="text-sm font-medium text-slate-900">Attendance Tracking</span>
                        <p className="text-xs text-slate-500">Players reply "yes/no/maybe" to confirm attendance</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <span className="text-sm font-medium text-slate-900">Sub Requests</span>
                        <p className="text-xs text-slate-500">Captains request subs, available players get notified</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <span className="text-sm font-medium text-slate-900">Schedule Changes</span>
                        <p className="text-xs text-slate-500">Cancellations and reschedules notify affected teams</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <span className="text-sm font-medium text-slate-900">Broadcast Messages</span>
                        <p className="text-xs text-slate-500">Send announcements to your entire league instantly</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <span className="text-sm font-medium text-slate-900">Score Updates</span>
                        <p className="text-xs text-slate-500">Game results shared with participating players</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/60 rounded-lg p-3 border border-ice-200">
                  <p className="text-xs text-slate-600">
                    <span className="font-semibold text-ice-700">Pro tip:</span> Set up your schedule, add players to rosters, and enter game scores.
                    IceOps handles the rest - sending reminders, tracking attendance, and keeping everyone informed automatically!
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sub Response Modal */}
      {respondingTo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Respond to Sub Request</h3>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
              <p className="text-xs font-medium text-orange-600 mb-1">{respondingTo.league_name}</p>
              <p className="text-sm font-medium text-orange-900">
                {respondingTo.home_team_name} vs {respondingTo.away_team_name}
              </p>
              <p className="text-sm text-orange-700 mt-1">
                {respondingTo.game_date && parseLocalDate(respondingTo.game_date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                {respondingTo.game_time && ` at ${formatTime(respondingTo.game_time)}`}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Message (optional)
              </label>
              <textarea
                value={respondMessage}
                onChange={(e) => setRespondMessage(e.target.value)}
                placeholder="Add a message for the captain..."
                className="w-full p-2 border border-slate-300 rounded-lg text-sm"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setRespondingTo(null); setRespondMessage('') }}
                className="flex-1 btn-secondary btn-sm"
                disabled={respondLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubResponse}
                className="flex-1 btn-warning btn-sm"
                disabled={respondLoading}
              >
                {respondLoading ? 'Sending...' : 'I Can Sub'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
